import { useState } from "react";
import { Check, Download, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api, downloadFile, errMsg } from "@/lib/api";

const INCLUDES = [
  ["Your brand & marketing foundations", "Mission (IKIGAI method), positioning statement, ideal customer profile, core transformation, tone of voice and content pillars."],
  ["Your Mini Marketing Plan", "Built on the Roots Growth Model: what to prioritise, what to hold off on, and your next 12 months, plus exactly who you're targeting."],
  ["Your 30-day content plan", "A full month of content ideas mapped to your pillars and audience."],
  ["A clear, structured document", "Everything delivered in a clean PDF you can reference, share, and use."],
];

export const buyMiniPlan = async (setBusy) => {
  setBusy(true);
  try {
    const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, item: "mini_plan" });
    window.location.href = data.checkout_url;
  } catch (e) { toast.error(errMsg(e)); setBusy(false); }
};

const Price = ({ price }) => (
  <div className="flex items-baseline gap-3" data-testid="mini-plan-price">
    <span className="font-serif text-4xl text-navy">£{price}</span>
    {price === 59 && <span className="text-xs font-mono uppercase text-pinkb">member price</span>}
  </div>
);

export const MiniPlanCard = () => (
  <Link to="/app/membership#mini-plan" data-testid="dashboard-mini-plan-card"
    className="fade-up mb-5 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-navy text-white p-6 hover:bg-navy/95 transition-colors">
    <div className="max-w-2xl">
      <div className="eyebrow !text-sun mb-1">Need more of a helping hand?</div>
      <p className="font-serif text-xl">Get a bespoke Mini Marketing Plan for your business, without paying for one-to-one consultancy.</p>
      <p className="text-sm text-white/70 mt-1">Members pay £59. Non-members pay £99.</p>
    </div>
    <span className="rounded-full bg-pinkb px-5 py-2 text-sm">See what's included</span>
  </Link>
);

export const MiniPlanOffer = ({ orders = [], price = 59 }) => {
  const [busy, setBusy] = useState(false);
  return (
    <div id="mini-plan" className="mt-8 rounded-3xl border-2 border-pinkb/40 bg-card p-8 lg:p-10" data-testid="mini-plan-offer">
      <div className="eyebrow mb-2 flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" />The Roots Method · Mini Marketing Plan</div>
      <h2 className="text-2xl sm:text-3xl text-navy">Struggling, and just need more of a helping hand?</h2>
      <p className="mt-3 text-muted-foreground max-w-2xl">A bespoke brand and marketing plan built around your business, your stage and what actually works. It's a great alternative to paying consultant fees for one-to-one sessions.</p>
      <ul className="mt-6 grid sm:grid-cols-2 gap-4">
        {INCLUDES.map(([t, d]) => (
          <li key={t} className="flex gap-3"><Check className="h-4 w-4 text-leaf mt-1 shrink-0" /><div><div className="font-medium">{t}</div><p className="text-sm text-muted-foreground">{d}</p></div></li>
        ))}
      </ul>
      <div className="mt-8 flex flex-wrap items-center justify-between gap-6">
        <div>
          <Price price={price} />
          <p className="text-sm text-muted-foreground mt-1" data-testid="mini-plan-price-note">{price === 59 ? "Your member price. Non-members pay £99." : "Paying members get it for £59. Non-member price: £99."} One-off payment, VAT included.</p>
        </div>
        <Button onClick={() => buyMiniPlan(setBusy)} disabled={busy} data-testid="mini-plan-buy-btn" className="rounded-full bg-pinkb hover:bg-pinkb/90 text-white px-8">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Get my Mini Marketing Plan
        </Button>
      </div>
      {orders.length > 0 && (
        <div className="mt-6 border-t border-border pt-4 text-sm" data-testid="mini-plan-orders">
          {orders.map((o) => (
            <p key={o.id} className="flex flex-wrap items-center gap-3">Your plan ordered {new Date(o.created_at).toLocaleDateString("en-GB")}: <span className="font-medium">{{ new: "received", in_progress: "in progress", delivered: "delivered" }[o.status]}</span>
              {!o.q_submitted && o.q_token && <Link to={`/plan-questionnaire/${o.q_token}`} data-testid={`mini-plan-questionnaire-${o.id}`} className="text-pinkb underline">Fill in your questionnaire</Link>}
              {o.q_submitted && !o.has_file && <span className="text-muted-foreground" data-testid={`mini-plan-q-sent-${o.id}`}>Questionnaire sent to Lindsay</span>}
              {o.has_file && <button onClick={() => downloadFile(`/plan-orders/${o.id}/file`, o.file_name || "mini-marketing-plan.pdf").catch((e) => toast.error(errMsg(e)))} data-testid={`mini-plan-download-${o.id}`} className="inline-flex items-center gap-1 text-pinkb hover:underline"><Download className="h-3.5 w-3.5" />Download PDF</button>}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};
